import { keyframes } from '@emotion/react'
import { makeStyles } from 'tss-react/mui'
import { fundingColors as C } from '../../theme/fundingColors'
import {
  FUNDING_FORM_FIELD_HEIGHT,
  fundingFormFieldBox,
  fundingPrimaryButton,
  fundingSecondaryButton,
} from '../../theme/fundingSharedStyles'
import { fundingFont, fundingType } from '../../theme/fundingTypography'

const skeletonShimmer = keyframes({
  '0%': { backgroundPosition: '100% 0' },
  '100%': { backgroundPosition: '-100% 0' },
})

export const useStyles = makeStyles()(() => ({
  /** Full width of the settings canvas (bottom bar stretches here). */
  pageShell: {
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    fontFamily: fundingFont,
    color: C.strongText,
    '& .MuiTypography-root': { fontFamily: fundingFont },
  },
  /**
   * Desktop-863 content column: max 1008, ~23px between sections.
   * Breadcrumbs + cards only — not the sticky bottom bar.
   */
  contentColumn: {
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    maxWidth: 1008,
    // Horizontal inset when canvas padding is 0 (template detail full-bleed banner).
    padding: '16px 40px 0',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 23,
    overflowX: 'hidden',
  },
  /** When the archived banner is above — small gap before breadcrumbs. */
  contentColumnAfterBanner: {
    paddingTop: 12,
  },
  /**
   * Desktop-867 sticky archived alert — edge-to-edge under the settings header,
   * no vertical gap before breadcrumbs / cards.
   */
  archiveBanner: {
    boxSizing: 'border-box',
    position: 'sticky',
    top: 0,
    zIndex: 3,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '6px 40px',
    margin: 0,
    borderRadius: 0,
    backgroundColor: C.archiveBannerBg,
  },
  archiveBannerText: {
    flex: '1 1 auto',
    minWidth: 0,
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: C.strongText,
  },
  archiveBannerAction: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: C.linkBlue,
    whiteSpace: 'nowrap',
    '& svg': { fontSize: 16, width: 16, height: 16, color: C.linkBlue },
    '&:hover': { textDecoration: 'underline' },
    '&:disabled': { opacity: 0.5, cursor: 'default', textDecoration: 'none' },
  },
  breadcrumbs: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
    ...fundingType.bodyMedium,
    color: C.mutedText,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
  breadcrumbLink: {
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    ...fundingType.bodyMedium,
    color: C.mutedText,
    '&:hover': { textDecoration: 'underline', color: C.darkText },
  },
  breadcrumbSep: {
    color: C.mutedText,
    fontSize: 12,
    width: 16,
    height: 16,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  breadcrumbSepCurrent: {
    color: C.darkText,
  },
  breadcrumbCurrent: {
    color: C.darkText,
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.1px',
  },
  card: {
    boxSizing: 'border-box',
    width: '100%',
    backgroundColor: C.cardBg,
    borderRadius: 8,
    border: `1px solid ${C.divider}`,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    fontFamily: fundingFont,
    overflow: 'hidden',
  },
  /** Top summary block — Figma Frame 29654: no border, gap 16 */
  summaryCard: {
    border: 'none',
    gap: 16,
    overflow: 'visible',
    justifyContent: 'center',
  },
  /** Metadata + eligibility + description (Figma Frame 1171276614 gap 12) */
  summaryFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    minWidth: 0,
  },
  /** AI prompt + composer + note (Figma gap 16 under title) */
  aiSectionBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    minWidth: 0,
  },
  cardTitleGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  iconBox: {
    width: 24,
    height: 24,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: C.iconBoxBg,
    borderRadius: 4,
    '& svg': { fontSize: 12, color: C.darkText },
  },
  cardTitle: {
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    color: C.darkText,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    // Figma Frame 1171276614: column gap 16, row gap 12
    columnGap: 16,
    rowGap: 12,
    width: '100%',
    '@media (max-width: 900px)': {
      gridTemplateColumns: '1fr 1fr',
    },
  },
  summaryField: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  summaryLabel: {
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    color: C.mutedText,
    margin: 0,
  },
  summaryValue: {
    fontFamily: fundingFont,
    // Compact vs Desktop-863 16/24 — 14/20 keeps hierarchy without oversized values
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: C.strongText,
    margin: 0,
    wordBreak: 'break-word',
  },
  summaryValueMuted: {
    color: C.mutedText,
  },
  usedInLink: {
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    color: C.linkBlue,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  nameInput: {
    flex: '1 1 auto',
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'none',
    padding: 0,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: C.strongText,
    cursor: 'text',
    borderBottom: '1px solid transparent',
    '&::placeholder': { color: C.mutedText, fontWeight: 400 },
    '&:focus': {
      borderBottomColor: C.linkBlue,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.7,
    },
  },
  nameEditButton: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    padding: 4,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: C.mutedText,
    borderRadius: 4,
    '&:hover:not(:disabled)': {
      color: C.strongText,
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  aiPromptLabel: {
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: C.mutedText,
  },
  aiSuggestionsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  regenerateButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    margin: 0,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    letterSpacing: '0.1px',
    color: C.linkBlue,
    '& svg': { fontSize: 12, color: C.linkBlue },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  statusPillToggle: {
    position: 'relative',
    boxSizing: 'border-box',
    width: 91,
    height: 30,
    padding: 0,
    border: `1px solid ${C.divider}`,
    borderRadius: 200,
    backgroundColor: C.statusTrack,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    '&:disabled': { opacity: 0.6, cursor: 'not-allowed' },
  },
  statusPillToggleOn: {},
  statusPillToggleLabel: {
    position: 'absolute',
    left: 8,
    right: 8,
    top: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingLeft: 28,
    fontFamily: fundingFont,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.5px',
    lineHeight: '12px',
    color: C.mutedText,
    pointerEvents: 'none',
  },
  statusPillToggleLabelOn: {
    justifyContent: 'flex-start',
    paddingLeft: 0,
    paddingRight: 28,
    color: C.successText,
  },
  statusPillToggleKnob: {
    position: 'absolute',
    left: 3,
    top: 2,
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: C.strongText,
    boxShadow:
      '0px 6.25px 9.375px 0px rgba(0,0,0,0.1), 0px 2.5px 3.75px 0px rgba(0,0,0,0.1)',
    transition: 'left 120ms ease, right 120ms ease',
    pointerEvents: 'none',
  },
  statusPillToggleKnobOn: {
    left: 'auto',
    right: 3,
  },
  divider: {
    width: '100%',
    height: 0,
    border: 'none',
    borderTop: `1px solid ${C.divider}`,
    margin: 0,
  },
  summaryActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  /**
   * Figma 1811:5966 — M3 small outlined button (Clone / View activity / Archive).
   * height 32, padding 4 12 4 4, border #B0BDC2, radius 8, icon 24 box + 12px glyph.
   */
  outlineButton: {
    boxSizing: 'border-box',
    height: 32,
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 12px 4px 4px',
    margin: 0,
    border: `1px solid ${C.stepUpcomingBorder}`,
    borderRadius: 8,
    backgroundColor: C.cardBg,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  outlineButtonIcon: {
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    flexShrink: 0,
    color: C.darkText,
    '& svg': {
      width: 12,
      height: 12,
      fontSize: 12,
      lineHeight: '12px',
      letterSpacing: '0.15px',
    },
  },
  outlineButtonLabel: {
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: C.strongText,
    textAlign: 'center' as const,
  },
  iconButton: {
    width: 24,
    height: 24,
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    color: C.mutedText,
    '& svg': { fontSize: 14 },
    '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
  },
  aiCard: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 192,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    // Desktop-863: 1px #041282 + soft glow
    border: `1px solid ${C.aiCardBorder}`,
    borderRadius: 20,
    backgroundColor: C.cardBg,
    boxShadow: '0px 0px 20px 0px rgba(77, 82, 138, 0.2)',
    overflow: 'hidden',
  },
  aiTextarea: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 56,
    flex: '1 1 auto',
    border: 'none',
    outline: 'none',
    resize: 'vertical',
    padding: '16px 16px 0',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: C.darkText,
    fontFamily: fundingFont,
    background: 'transparent',
    '&::placeholder': { color: C.mutedText },
  },
  aiSuggestions: {
    borderTop: `1px solid ${C.aiSuggestionsBorder}`,
    backgroundImage:
      'linear-gradient(138.28deg, rgba(4, 18, 130, 0.08) 0%, rgba(65, 79, 140, 0.08) 61.61%, rgba(166, 96, 122, 0.08) 111.43%)',
    borderRadius: '0 0 10px 10px',
    // Match aiTextarea horizontal padding so label/chips align with prompt text.
    padding: '6px 16px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
    boxSizing: 'border-box',
  },
  aiSuggestionsLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontFamily: fundingFont,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: '14px',
    color: C.mutedText,
    margin: 0,
    padding: 0,
    // Text first (sparkle after) so left edge matches the textarea content.
    justifyContent: 'flex-start',
  },
  aiSparkle: {
    flexShrink: 0,
    fontSize: 11,
    lineHeight: '11px',
    letterSpacing: '0.15px',
    backgroundImage: `linear-gradient(114.88deg, ${C.aiGradientStart} 31.24%, ${C.aiGradientMid} 53.58%, ${C.aiGradientEnd} 78.99%)`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
  },
  chips: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 8,
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
  },
  chip: {
    border: '0.5px solid #000',
    backgroundColor: C.cardBg,
    borderRadius: 20,
    padding: '4px 12px',
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: C.darkText,
    cursor: 'pointer',
    flex: '0 1 auto',
    maxWidth: 240,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fieldBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  fieldLabel: {
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: C.darkText,
  },
  input: {
    ...fundingFormFieldBox,
    width: '100%',
    padding: '10px 16px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: C.darkText,
    outline: 'none',
    backgroundColor: C.cardBg,
    '&::placeholder': { color: C.mutedText },
  },
  bottomBar: {
    // Sticky full width of pageShell (canvas inner) — not capped at 1008 with cards.
    position: 'sticky',
    bottom: 0,
    zIndex: 20,
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: C.cardBg,
    boxShadow: 'inset 0px 1px 0px 0px #ecedef',
    marginTop: 23,
  },
  bottomBarInner: {
    boxSizing: 'border-box',
    width: '100%',
    // Desktop-863 Bottom nav: padding 12px 40px
    padding: '12px 40px',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  bottomBarActions: {
    display: 'flex',
    alignItems: 'center',
    // Figma Bottom nav action group gap 24
    gap: 24,
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
    minHeight: 30,
    padding: '0 8px',
    borderRadius: 4,
    backgroundColor: C.statusActive,
    color: C.onPrimary,
    ...fundingType.chip,
    fontWeight: 600,
    letterSpacing: '0.4px',
  },
  statusPillMuted: {
    backgroundColor: C.statusMuted,
  },
  nameEditIcon: {
    fontSize: 14,
  },
  autoSubmitBanner: {
    boxSizing: 'border-box',
    width: '100%',
    padding: '8px 12px',
    borderRadius: 4,
    backgroundColor: C.noteBannerBg,
    border: 'none',
    // Figma Note body: Inter 14/20 #191C1D
    '& [data-testid="auto-submit-limit-note"]': {
      fontFamily: fundingFont,
      fontSize: 14,
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0.25px',
      color: C.onSurface,
    },
  },
  metaReadonlyBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  metaReadonlyText: {
    margin: 0,
    fontFamily: fundingFont,
    // Match summaryValue (e.g. Created by: "Sai Praveen on Oct 16, 2026")
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    color: C.strongText,
    whiteSpace: 'pre-wrap',
  },
  resetDefaultButton: {
    border: 'none',
    background: 'none',
    padding: '8px 0',
    cursor: 'pointer',
    fontFamily: fundingFont,
    color: C.linkBlue,
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: '0.15px',
    lineHeight: '24px',
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  secondaryButton: {
    ...fundingSecondaryButton,
    height: 40,
    padding: '8px 24px',
    border: `1px solid ${C.stepUpcomingBorder}`,
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  primaryButton: {
    ...fundingPrimaryButton,
    height: 40,
    padding: '8px 24px',
    // Keep Labra orange fill with dark label for contrast (Jai feedback).
    color: C.darkText,
    '&:disabled': {
      backgroundColor: 'rgba(29, 27, 32, 0.12)',
      color: 'rgba(33, 33, 33, 0.38)',
      cursor: 'not-allowed',
      opacity: 1,
    },
  },
  autosaveHint: {
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '20px',
    color: C.mutedText,
    alignSelf: 'center',
  },
  floatField: {
    ...fundingFormFieldBox,
    position: 'relative' as const,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    border: `1px solid ${C.buttonBorder}`,
    borderRadius: 4,
    backgroundColor: C.cardBg,
    '&:focus-within': {
      borderColor: C.linkBlue,
    },
  },
  floatFieldFloated: {},
  floatFieldError: {
    borderColor: C.errorText,
  },
  floatLabelInField: {
    position: 'absolute' as const,
    left: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
    pointerEvents: 'none' as const,
    transition: 'top 120ms ease, transform 120ms ease, font-size 120ms ease',
    fontFamily: fundingFont,
    fontSize: 14,
    color: C.mutedText,
    backgroundColor: C.cardBg,
    padding: '0 2px',
  },
  floatLabelFloated: {
    top: 0,
    transform: 'translateY(-50%)',
    fontSize: 12,
    lineHeight: '16px',
  },
  floatInput: {
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: fundingFont,
    fontSize: 14,
    lineHeight: '20px',
    color: C.darkText,
    padding: 0,
    '&:disabled': { color: C.mutedText },
  },
  resourcesTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  optionalBadge: {
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '20px',
    color: C.mutedText,
  },
  /** One panel: type+upload rows, then file list. */
  uploadPanel: {
    boxSizing: 'border-box' as const,
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
    padding: 16,
    border: `1px solid ${C.divider}`,
    borderRadius: 8,
    backgroundColor: C.cardBg,
  },
  uploadAddRows: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 12,
    width: '100%',
  },
  /** Document type | Upload files side by side. */
  uploadAddRow: {
    boxSizing: 'border-box' as const,
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    gap: 12,
    width: '100%',
  },
  uploadPanelType: {
    boxSizing: 'border-box' as const,
    width: '100%',
    maxWidth: 280,
    minWidth: 200,
    flex: '0 1 280px',
  },
  uploadFileList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    width: '100%',
    margin: 0,
    padding: 0,
    listStyle: 'none' as const,
  },
  uploadFileRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
    width: '100%',
  },
  uploadFileMeta: {
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  uploadFileTypeLabel: {
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '16px',
    color: C.mutedText,
  },
  documentName: {
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    color: C.darkText,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  uploadActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: '1 1 auto',
    minWidth: 0,
    flexWrap: 'wrap' as const,
  },
  uploadInlineFiles: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: '1 1 160px',
    minWidth: 0,
    flexWrap: 'wrap' as const,
  },
  uploadInlineFile: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    maxWidth: '100%',
    minWidth: 0,
    padding: '4px 8px',
    borderRadius: 4,
    backgroundColor: C.metricBg,
  },
  uploadButton: {
    boxSizing: 'border-box',
    height: FUNDING_FORM_FIELD_HEIGHT,
    minWidth: 132,
    padding: '8px 16px',
    borderRadius: 4,
    border: `1px solid ${C.stepUpcomingBorder}`,
    backgroundColor: C.cardBg,
    fontFamily: fundingFont,
    color: C.strongText,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: '0.1px',
    lineHeight: '20px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  uploadSpinner: {
    color: C.linkBlue,
  },
  dropzoneInfo: {
    fontSize: 16,
    color: C.mutedText,
    cursor: 'help',
  },
  addUploadRowButton: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: 'none',
    background: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: fundingFont,
    color: C.linkBlue,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',
    '& svg': { fontSize: 12 },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  sowError: {
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '16px',
    color: C.errorText,
  },
  hiddenFileInput: {
    display: 'none',
  },
  removeDocButton: {
    flexShrink: 0,
    border: 'none',
    background: 'transparent',
    color: C.mutedText,
    cursor: 'pointer',
    padding: 4,
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
  },
  loading: {
    margin: 0,
    fontFamily: fundingFont,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    color: C.mutedText,
  },
  skeletonBar: {
    display: 'block',
    borderRadius: 4,
    background: `linear-gradient(
      90deg,
      ${C.metricBg} 0%,
      ${C.divider} 45%,
      ${C.metricBg} 90%
    )`,
    backgroundSize: '200% 100%',
    animation: `${skeletonShimmer} 1.35s ease-in-out infinite`,
  },
  skeletonLabel: {
    height: 12,
    width: '40%',
    marginBottom: 6,
  },
  skeletonValue: {
    height: 16,
    width: '78%',
  },
  skeletonBlock: {
    height: 56,
    width: '100%',
  },
  skeletonTextarea: {
    height: 96,
    width: '100%',
    borderRadius: 8,
  },
  skeletonChipRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 8,
    marginTop: 12,
  },
  skeletonChip: {
    height: 28,
    width: 140,
    borderRadius: 14,
  },
  skeletonFieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: 16,
    rowGap: 16,
    width: '100%',
  },
  skeletonField: {
    height: FUNDING_FORM_FIELD_HEIGHT,
    width: '100%',
  },
  skeletonMetaStack: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
    width: '100%',
    marginTop: 16,
  },

  addFieldRow: {
    marginTop: 16,
    maxWidth: 420,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  addFieldButton: {
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    boxSizing: 'border-box',
    height: FUNDING_FORM_FIELD_HEIGHT,
    padding: '8px 16px',
    borderRadius: 4,
    border: `1px solid ${C.stepUpcomingBorder}`,
    backgroundColor: C.cardBg,
    fontFamily: fundingFont,
    color: C.strongText,
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: '0.1px',
    lineHeight: '20px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: C.metricBg },
  },
}))
